import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

const ProfileForm = ({ formData, onChange, onSubmit, saving, dirty, fieldErrors = {} }) => {
    return (
        <section className="bg-white dark:bg-slate-800/50 dark:backdrop-blur-xl border-slate-200 dark:border-white/10 shadow-xl dark:shadow-2xl rounded-3xl p-8 transition-all">
            <h3 className="text-lg font-semibold mb-6">
                Professional Details
            </h3>
            <form onSubmit={onSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label htmlFor="display_name" className="text-sm font-medium text-slate-600 dark:text-slate-400">Display Name</label>
                        <input
                            id="display_name"
                            type="text"
                            name="display_name"
                            value={formData.display_name}
                            onChange={onChange}
                            placeholder="e.g. Alex Chen"
                            className={`w-full bg-white dark:bg-slate-900/50 border-slate-300 dark:border-white/10 text-slate-900 dark:text-white rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all ${
                                fieldErrors.display_name ? 'border-red-500 dark:border-red-500' : ''
                            }`}
                        />
                        {fieldErrors.display_name && (
                            <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                                {Array.isArray(fieldErrors.display_name) 
                                    ? fieldErrors.display_name[0] 
                                    : fieldErrors.display_name}
                            </p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                        <label htmlFor="job_title" className="text-sm font-medium text-slate-600 dark:text-slate-400">Job Title</label>
                            <span className={`text-xs ${formData.job_title?.length > 40 ? 'text-orange-500' : formData.job_title?.length > 0 ? 'text-gray-500' : 'text-transparent'}`}>
                                {formData.job_title?.length || 0}/50
                            </span>
                        </div>
                        <input
                            id="job_title"
                            type="text"
                            name="job_title"
                            value={formData.job_title}
                            onChange={onChange}
                            maxLength={50}
                            placeholder="e.g. Senior Product Designer"
                            className={`w-full bg-white dark:bg-slate-900/50 border-slate-300 dark:border-white/10 text-slate-900 dark:text-white rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all ${
                                fieldErrors.job_title || formData.job_title?.length > 50 
                                    ? 'border-red-500 dark:border-red-500' 
                                    : ''
                            }`}
                        />
                        {fieldErrors.job_title && (
                            <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                                {Array.isArray(fieldErrors.job_title) 
                                    ? fieldErrors.job_title[0] 
                                    : fieldErrors.job_title}
                            </p>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                    <label htmlFor="phone_number" className="text-sm font-medium text-slate-600 dark:text-slate-400">Phone Number</label>
                        <span className={`text-xs ${formData.phone_number?.length > 15 ? 'text-orange-500' : formData.phone_number?.length > 0 ? 'text-gray-500' : 'text-transparent'}`}>
                            {formData.phone_number?.length || 0}/20
                        </span>
                    </div>
                    <input
                        id="phone_number"
                        type="text"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={onChange}
                        maxLength={20}
                        placeholder="e.g. +1 (555) 000-0000"
                        className={`w-full bg-white dark:bg-slate-900/50 border-slate-300 dark:border-white/10 text-slate-900 dark:text-white rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all ${
                            fieldErrors.phone_number || formData.phone_number?.length > 20 
                                ? 'border-red-500 dark:border-red-500' 
                                : ''
                        }`}
                    />
                    {fieldErrors.phone_number && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            {Array.isArray(fieldErrors.phone_number) 
                                ? fieldErrors.phone_number[0] 
                                : fieldErrors.phone_number}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                    <label htmlFor="bio" className="text-sm font-medium text-slate-600 dark:text-slate-400">Bio</label>
                        <span className={`text-xs ${formData.bio?.length > 80 ? 'text-orange-500' : formData.bio?.length > 0 ? 'text-gray-500' : 'text-transparent'}`}>
                            {formData.bio?.length || 0}/100
                        </span>
                    </div>
                    <textarea
                        id="bio"
                        name="bio"
                        value={formData.bio}
                        onChange={onChange}
                        maxLength={100}
                        rows="4"
                        placeholder="Tell us a bit about yourself..."
                        className={`w-full bg-white dark:bg-slate-900/50 border-slate-300 dark:border-white/10 text-slate-900 dark:text-white rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all resize-none ${
                            fieldErrors.bio || formData.bio?.length > 100 
                                ? 'border-red-500 dark:border-red-500' 
                                : ''
                        }`}
                    ></textarea>
                    {fieldErrors.bio && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                            {Array.isArray(fieldErrors.bio) 
                                ? fieldErrors.bio[0] 
                                : fieldErrors.bio}
                        </p>
                    )}
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving || !dirty}
                        className="bg-indigo-600 hover:bg-indigo-500 active:scale-95 disabled:opacity-50 disabled:active:scale-100 text-white font-semibold px-8 py-2.5 rounded-xl flex items-center gap-2 shadow-lg shadow-indigo-500/20 transition-all"
                    >
                        {saving ? (
                            <span className="size-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        ) : (
                            <CheckIcon className="size-5" />
                        )}
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default ProfileForm;
